/* eslint-disable jsx-a11y/anchor-is-valid */
import { Form as AntdForm } from "sunday-component/node_modules/antd";
import DatePickerForm from "src/components/Common/Form/DatePickerForm";
import TextBoxForm, {
  TextBoxErrorText,
} from "src/components/Common/Form/TextBoxForm";
import { USER_CONSENT, AFFILIATE_FORM_FIELDS } from "src/constants/userProfile";
import { Form } from "sunday-component";
import useBreakpoint from "antd/lib/grid/hooks/useBreakpoint";
import { EMAIL_PATTERN } from "src/utils/regEx";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Row } from "antd";
import CheckBox from "src/components/Common/CheckBox";
import {
  validateAge,
  validateRequiredExcludeFalseRule,
  validateRequiredRule,
} from "src/utils/validationRules";
import ErrorMessageWithIcon from "src/components/Common/Form/ErrorMessageWithIcon";
import { RuleObject } from "antd/lib/form";
import Link from "next/link";
import LinkBody from "src/components/Common/LinkBody";
import { useRouter } from "next/router";
import { User } from "src/types/user";
import dayjs from "dayjs";
import Toast from "src/components/Common/Toast/Toast";
import { registerAffiliate } from "src/api/affiliate";
import ConfirmDialog from "src/components/Common/ConfirmDialog/ConfirmDialog";
import { formatDateText } from "src/utils/date";
import {
  CardDetail,
  SignupPageItem,
  UserButton,
  ModalContent,
  ExtraText,
  SubTitleText,
  ConsentText,
  BannerImg,
  CenterTitle,
  NextStepContainer,
  NextStepText,
  CloseDialogTitle,
  RegisterButton,
  Container,
  NextStepItemText,
  NextStepList,
  RegisterLabel,
  RegisterModalContent,
  SuccessModal,
  RegisterModal,
} from "./RegisterSection.styled";
import { ShareLinkTextBox } from "../ShareLinkTextBox/ShareLinkTextBox";

type RegisterSectionType = {
  user: User | null;
};

export const RegisterSection = ({ user }: RegisterSectionType) => {
  const [form] = AntdForm.useForm();
  const { xs } = useBreakpoint();
  const router = useRouter();
  const { locale } = router;

  const [isRegisterModalVisible, setIsRegisterModalVisible] =
    useState<boolean>(false);
  const [isSuccessModalVisible, setIsSuccessModalVisible] =
    useState<boolean>(false);
  const [isCloseModalVisible, setIsCloseModalVisible] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [sRefCode, setSRefCode] = useState<string>();
  const [isSubmitButtonDisable, setIsSubmitButtonDisable] =
    useState<boolean>(true);
  const { t } = useTranslation(["user-profile", "affiliate", "form", "common"]);

  const validateEmail = (_: any, value: string, submitError: any) => {
    if (value && value.trim() !== "" && !EMAIL_PATTERN.test(value)) {
      return submitError(
        <TextBoxErrorText>
          {t(`form:validation.pleaseEnterValid`, {
            field: t("editPersonalInfo.email.label"),
          })}
        </TextBoxErrorText>
      );
    }
    return submitError();
  };

  const onFieldsChange = (_: any, allFields: any[]) => {
    setIsSubmitButtonDisable(
      allFields.filter(({ errors, value }) => errors.length || !value).length >
        0
    );
  };

  const onWriteClipboard = () => {
    const shareLinkUrl = `${process.env.NEXT_PUBLIC_BASE_PATH}/?sRefCode=${sRefCode}`;
    navigator.clipboard.writeText(shareLinkUrl);
    Toast(t("common:toast.copy"));
  };

  const onSubmit = async (input: any) => {
    try {
      setIsLoading(true);
      const formattedInput = {
        ...input,
        [AFFILIATE_FORM_FIELDS.DOB]: input[AFFILIATE_FORM_FIELDS.DOB]
          ? formatDateText(input[AFFILIATE_FORM_FIELDS.DOB])
          : undefined,
      };
      const res = await registerAffiliate(formattedInput);
      setSRefCode(res.sRefCode);
      setIsRegisterModalVisible(false);
      setIsSuccessModalVisible(true);
    } catch (e) {
      router.reload();
    } finally {
      setIsLoading(false);
    }
  };

  if (!user || user.affiliate?.sRefCode) {
    return <></>;
  }

  return (
    <Container>
      <RegisterButton
        type="primary"
        onClick={() => {
          setIsRegisterModalVisible(true);
        }}
      >
        <RegisterLabel>{t("affiliate:register.submit")}</RegisterLabel>
      </RegisterButton>
      <ConfirmDialog
        title={
          <CloseDialogTitle>
            {t("affiliate:register.closeModal.title")}
          </CloseDialogTitle>
        }
        description={t("affiliate:register.closeModal.description")}
        isVisible={isCloseModalVisible}
        confirmText={t("affiliate:register.closeModal.continue")}
        rejectText={t("affiliate:register.closeModal.close")}
        rejectButtonProps={{
          onClick: () => {
            setIsCloseModalVisible(false);
            setIsRegisterModalVisible(false);
          },
        }}
        confirmButtonProps={{
          onClick: () => {
            setIsCloseModalVisible(false);
          },
        }}
        fullWidthButton
        centered
      />
      <SuccessModal
        bodyRadius="16px 16px 0px 0px"
        closable={false}
        footer={
          <UserButton
            onClick={() => {
              setIsSuccessModalVisible(false);
              router.reload();
            }}
          >
            {t("affiliate:register.success.close")}
          </UserButton>
        }
        visible={isSuccessModalVisible}
        width={303}
        maskClosable={false}
        centered
      >
        <BannerImg
          src="https://static.easysunday.com/retail/affiliate/content/register-modal-banner-02.png"
          alt="success-register-banner"
        />
        <ModalContent>
          <CenterTitle>{t("affiliate:register.success.title")}</CenterTitle>
          <ShareLinkTextBox
            shareLinkUrl={`https://retail.easysunday.com/?sRefCode=${sRefCode}`}
            onWriteClipboard={onWriteClipboard}
          />
          <NextStepContainer>
            <NextStepText>
              {t("affiliate:register.success.nextStep.title")}
            </NextStepText>
            <NextStepItemText>
              <NextStepList type="1">
                <li>{t("affiliate:register.success.nextStep.stepOne")}</li>
                <li>{t("affiliate:register.success.nextStep.stepTwo")}</li>
                <li>{t("affiliate:register.success.nextStep.stepThree")}</li>
              </NextStepList>
            </NextStepItemText>
          </NextStepContainer>
        </ModalContent>
      </SuccessModal>
      <RegisterModal
        title={<CenterTitle>{t("affiliate:register.title")}</CenterTitle>}
        footer={
          <UserButton
            disabled={isSubmitButtonDisable}
            loading={isLoading}
            onClick={() => {
              form.submit();
            }}
          >
            {t("affiliate:register.submit")}
          </UserButton>
        }
        visible={isRegisterModalVisible}
        onCancel={() => {
          setIsCloseModalVisible(true);
        }}
        width={!xs ? 444 : "100vw"}
        maskClosable={false}
        centered={!xs}
      >
        <BannerImg
          src="https://static.easysunday.com/retail/affiliate/content/register-modal-banner-01.png"
          alt="register-banner"
        />
        <RegisterModalContent>
          <CardDetail>
            <SubTitleText>{t("affiliate:register.subTitle")}</SubTitleText>
            <Form
              form={form}
              onFinish={onSubmit}
              onFieldsChange={onFieldsChange}
              initialValues={{
                [AFFILIATE_FORM_FIELDS.FIRST_NAME]: user?.profile.firstname,
                [AFFILIATE_FORM_FIELDS.LAST_NAME]: user?.profile.lastname,
                [AFFILIATE_FORM_FIELDS.DOB]: user?.profile.dob
                  ? dayjs(user.profile.dob)
                  : undefined,
                contacts: {
                  [AFFILIATE_FORM_FIELDS.PRIMARY_EMAIL]:
                    user?.profile.contacts.primaryemail,
                },
              }}
            >
              <SignupPageItem
                name={AFFILIATE_FORM_FIELDS.FIRST_NAME}
                rules={[
                  {
                    required: true,
                    message: (
                      <TextBoxErrorText>
                        {t("form:validation.required", {
                          field: t("editPersonalInfo.firstName.label"),
                        })}
                      </TextBoxErrorText>
                    ),
                  },
                ]}
              >
                <TextBoxForm
                  key={AFFILIATE_FORM_FIELDS.FIRST_NAME}
                  placeholder={t("editPersonalInfo.firstName.placeholder")}
                  label={t("editPersonalInfo.firstName.label")}
                />
              </SignupPageItem>
              <SignupPageItem
                name={AFFILIATE_FORM_FIELDS.LAST_NAME}
                rules={[
                  {
                    required: true,
                    message: (
                      <TextBoxErrorText>
                        {t("form:validation.required", {
                          field: t("editPersonalInfo.lastName.label"),
                        })}
                      </TextBoxErrorText>
                    ),
                  },
                ]}
              >
                <TextBoxForm
                  key={AFFILIATE_FORM_FIELDS.LAST_NAME}
                  placeholder={t("editPersonalInfo.lastName.placeholder")}
                  label={t("editPersonalInfo.lastName.label")}
                />
              </SignupPageItem>
              <SignupPageItem
                extra={
                  <ExtraText>{t("affiliate:register.requireAge20")}</ExtraText>
                }
                name={AFFILIATE_FORM_FIELDS.DOB}
                validateFirst
                rules={[
                  {
                    validator: (_: RuleObject, value: any) => {
                      if (!validateRequiredRule(value)) {
                        return Promise.reject();
                      }
                      return Promise.resolve();
                    },
                    message: (
                      <TextBoxErrorText>
                        {t("form:validation.selectedRequired", {
                          field: t("editPersonalInfo.dob.label"),
                        })}
                      </TextBoxErrorText>
                    ),
                  },
                  ...validateAge(
                    [
                      {
                        rule: "ageByDay0",
                        params: { min: 20 },
                      },
                    ],
                    t,
                    new Date().toISOString(),
                    TextBoxErrorText,
                    t("affiliate:register.dob.validationAgeError")
                  ),
                ]}
              >
                <DatePickerForm
                  style={{ height: "48px" }}
                  key={AFFILIATE_FORM_FIELDS.DOB}
                  placeholder={t("editPersonalInfo.dob.placeholder")}
                  disabledFuture
                  label={t("editPersonalInfo.dob.label")}
                />
              </SignupPageItem>
              <SignupPageItem
                name={["contacts", AFFILIATE_FORM_FIELDS.PRIMARY_EMAIL]}
                rules={[
                  {
                    required: true,
                    message: (
                      <TextBoxErrorText>
                        {t("form:validation.required", {
                          field: t("editPersonalInfo.email.label"),
                        })}
                      </TextBoxErrorText>
                    ),
                  },
                  { min: 0, max: 250 },
                  { validator: validateEmail },
                ]}
              >
                <TextBoxForm
                  key={AFFILIATE_FORM_FIELDS.EMAIL}
                  placeholder={t("editPersonalInfo.email.placeholder")}
                  label={t("editPersonalInfo.email.label")}
                />
              </SignupPageItem>
              <SignupPageItem
                name={["consents", USER_CONSENT.SUNDAY_AFFILIATE]}
                valuePropName="checked"
                rules={[
                  {
                    validator: async (_: RuleObject, value: boolean) => {
                      if (!validateRequiredExcludeFalseRule(value))
                        return Promise.reject();
                      return Promise.resolve();
                    },
                    message: (
                      <ErrorMessageWithIcon
                        errorMessage={t(
                          "affiliate:register.sundayAffiliateConsent.validationError"
                        )}
                      />
                    ),
                  },
                ]}
              >
                <CheckBox
                  label={
                    <Row justify="start">
                      <ConsentText>
                        {t(
                          "affiliate:register.sundayAffiliateConsent.preLabel"
                        )}
                        <Link
                          href={`https://static.easysunday.com/retail/affiliate/pdf/consent_tc_${locale}_20240923.pdf`}
                          passHref
                        >
                          <a target="#!">
                            <LinkBody level={7}>
                              {t(
                                "affiliate:register.sundayAffiliateConsent.label"
                              )}
                            </LinkBody>
                          </a>
                        </Link>
                        {t(
                          "affiliate:register.sundayAffiliateConsent.postLabel"
                        )}
                      </ConsentText>
                    </Row>
                  }
                />
              </SignupPageItem>
            </Form>
          </CardDetail>
        </RegisterModalContent>
      </RegisterModal>
    </Container>
  );
};
